export const CONTINUOUS_ASSESSMENT_PERIOD = "Continuous assessment";
export const LEGACY_MID_TERM_PERIOD = "Mid-term";

export function isContinuousAssessmentPeriod(value) {
    const period = String(value || "").trim();
    return period === CONTINUOUS_ASSESSMENT_PERIOD || period === LEGACY_MID_TERM_PERIOD;
}

/** Normalize stored/selected period values (legacy Mid-term → Continuous assessment). */
export function canonicalReportPeriod(value) {
    const period = String(value || "").trim();
    if (period === LEGACY_MID_TERM_PERIOD) return CONTINUOUS_ASSESSMENT_PERIOD;
    return period;
}

/** DB / API filters should match legacy rows until data is migrated. */
export function reportPeriodsForDbMatch(value) {
    const canonical = canonicalReportPeriod(value);
    if (canonical === CONTINUOUS_ASSESSMENT_PERIOD) {
        return [CONTINUOUS_ASSESSMENT_PERIOD, LEGACY_MID_TERM_PERIOD];
    }
    return [canonical || "End of term"];
}

export function reportPeriodDisplayLabel(value) {
    if (isContinuousAssessmentPeriod(value)) return CONTINUOUS_ASSESSMENT_PERIOD;
    return String(value || "").trim();
}

export function allowedReportPeriods(educationLevel) {
    const level = String(educationLevel || "").trim();
    if (level === "Primary") return ["End of term"];
    if (level === "Secondary") return [CONTINUOUS_ASSESSMENT_PERIOD, "End of term"];
    return [CONTINUOUS_ASSESSMENT_PERIOD, "End of term"];
}

export function defaultReportPeriod(educationLevel) {
    const list = allowedReportPeriods(educationLevel);
    return list[list.length - 1] || "End of term";
}

export function isReportPeriodAllowed(educationLevel, reportPeriod) {
    const period = canonicalReportPeriod(reportPeriod);
    return allowedReportPeriods(educationLevel).includes(period);
}

export const STUDENT_PERIOD_TABS = [
    { key: "classTests", label: "Class test", reportPeriod: "Class test" },
    {
        key: "midTerm",
        label: CONTINUOUS_ASSESSMENT_PERIOD,
        reportPeriod: CONTINUOUS_ASSESSMENT_PERIOD,
    },
    { key: "endOfTerm", label: "End of term", reportPeriod: "End of term" },
];

export function isClassTestExamLabel(examLabel) {
    const exam = String(examLabel || "").trim();
    if (!exam) return false;
    if (exam === "Class test" || exam.startsWith("Class test")) return true;
    return false;
}

export function extractYearNumbers(value) {
    return (String(value || "").match(/\d{4}/g) || []).map(Number);
}

export function academicYearsMatch(a, b) {
    const left = String(a ?? "").trim();
    const right = String(b ?? "").trim();
    if (!left || !right) return true;
    if (left === right) return true;
    const yearsLeft = extractYearNumbers(left);
    const yearsRight = extractYearNumbers(right);
    if (!yearsLeft.length || !yearsRight.length) return left === right;
    return yearsLeft.some((year) => yearsRight.includes(year));
}

export function examResultMatchesTermYear(row, term, academicYear) {
    const rowTerm = String(row?.term ?? "").trim();
    const rowYear = String(row?.academicYear ?? "").trim();
    const targetTerm = String(term ?? "").trim();
    const targetYear = String(academicYear ?? "").trim();
    if (!targetTerm && !targetYear) return true;
    if (targetTerm && rowTerm && rowTerm !== targetTerm) return false;
    if (targetYear && rowYear && !academicYearsMatch(rowYear, targetYear)) return false;
    return true;
}

export function rowBelongsToSchoolCurrentTerm(row, ctx = {}) {
    const schoolTerm = String(ctx.currentTerm || "").trim();
    const schoolYear = String(ctx.currentAcademicYear || "").trim();
    if (!schoolTerm && !schoolYear) return true;
    return examResultMatchesTermYear(row, schoolTerm, schoolYear);
}

/** Student portal may show marks that were published or stored before the portal flag existed. */
export function isExamResultVisibleOnPortal(row, options = {}) {
    if (!rowBelongsToSchoolCurrentTerm(row, options)) return false;
    if (row?.archivedAt) return false;
    if (Boolean(row?.publishedToPortal)) return true;
    if (row?.publishedAt) return true;
    if (String(row?.classBundleId || "").trim()) return true;
    const term = String(row?.term ?? "").trim();
    const year = String(row?.academicYear ?? "").trim();
    if (!term && !year && row?.marksObtained != null) return true;
    return false;
}

export function submissionMatchesReportPeriod(row, reportPeriod) {
    const period = canonicalReportPeriod(reportPeriod || "End of term");
    const stored = canonicalReportPeriod(row?.reportPeriod || "");
    if (row?.reportPeriod) return stored === period;
    const exam = String(row?.examLabel || "").trim();
    if (exam === period) return true;
    if (canonicalReportPeriod(exam) === period) return true;
    if (!exam || exam === "Assessment" || exam === "Class test") return true;
    if (exam.startsWith("Class test")) return true;
    return false;
}
